<template>
    <div class="date-field-container">
        <DatePicker v-model="inputValue" :disabled="disabled" :name="name" :id="id" :minDate="parsedMin"
            :maxDate="parsedMax" :placeholder="placeholder" :manualInput="true" dateFormat="dd-mm-yy" showIcon
            class="custom-datepicker" :showButtonBar="true" @clear-click="handleClear"
            @input="onPickerInput" @blur="blurInput" />
        <span v-if="shouldShowAge && calculatedAge" class="blue-bg-span age-display">{{ calculatedAge }}</span>
    </div>
</template>

<script>
import { DatePicker } from 'primevue'

export default {
    name: 'DateField',
    components: {
        DatePicker
    },
    emits: ['update:modelValue', 'input', 'onInput', 'blurInput'],
    props: {
        label: String,
        required: Boolean,
        modelValue: String,
        disabled: Boolean,
        name: String,
        id: [String, Number],
        min: String,
        max: {
            type: String,
            default: '9999-12-31'
        },
        placeholder: {
            type: String,
            default: 'DD-MM-YYYY'
        }
    },
    data() {
        return {
            internalDate: this.parseLocalDate(this.modelValue)
        }
    },
    computed: {
        inputValue: {
            get() {
                return this.parseLocalDate(this.modelValue)
            },
            set(val) {
                this.$emit('update:modelValue', this.formatToYMD(val))
            }
        },
        parsedMin() {
            return this.parseLocalDate(this.min)
        },
        parsedMax() {
            return this.parseLocalDate(this.max)
        },
        calculatedAge() {
            if (!this.modelValue) return null
            return this.calculateAge(this.modelValue)
        },
        shouldShowAge() {
            return ['dob', 'dateOfBirth'].includes(this.name) ||
                (this.name && (this.name.toLowerCase().includes('dob') || this.name.toLowerCase().includes('dateofbirth')))
        }
    },
    watch: {
        modelValue(newVal) {
            this.internalDate = this.parseLocalDate(newVal)
        }
    },
    methods: {
        /**
         * PrimeVue drives v-model while typing; we only mask digits in the native input
         * and forward the event so parents (e.g. getValidation) receive a real input event.
         * Do not gate model updates on 8 digits — that breaks manual entry when the parent re-renders.
         */
        onPickerInput(e) {
            if (e?.target && typeof e.target.value === 'string') {
                let value = e.target.value.replace(/\D/g, '')
                if (value.length > 4) {
                    value = value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4, 8)
                } else if (value.length > 2) {
                    value = value.slice(0, 2) + '-' + value.slice(2, 4)
                }
                e.target.value = value
            }
            this.$emit('input', e)
            this.$emit('onInput', e)
        },
        blurInput(event) {
            const originalEvent = event.originalEvent || event
            const formatted = this.getFormattedDate(originalEvent.target.value)
            originalEvent.target.value = formatted
            this.$emit('blurInput', originalEvent)
        },
        getFormattedDate(raw) {
            let value = raw.replace(/\D/g, '');
            if (value.length > 4) {
                return value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4, 8);
            } else if (value.length > 2) {
                return value.slice(0, 2) + '-' + value.slice(2, 4);
            }
            return value;
        },
        parseLocalDate(dateStr) {
            if (!dateStr || typeof dateStr !== 'string') return null

            const parts = dateStr.split('-').map(Number)

            if (parts[0] > 31) {
                const [y, m, d] = parts
                return new Date(y, m - 1, d)
            } else {
                const [d, m, y] = parts
                return new Date(y, m - 1, d)
            }
        },
        formatToYMD(dateObj) {
            if (!dateObj) return ''
            const y = dateObj.getFullYear()
            const m = String(dateObj.getMonth() + 1).padStart(2, '0')
            const d = String(dateObj.getDate()).padStart(2, '0')
            return `${y}-${m}-${d}`
        },
        handleClear() {
            this.inputValue = null
        },
        calculateAge(dateString) {
            if (!dateString) return null
            const today = new Date()
            const birth = new Date(dateString)
            if (birth > today) return null

            today.setHours(0, 0, 0, 0)
            birth.setHours(0, 0, 0, 0)
            const daysDiff = Math.floor((today - birth) / (24 * 60 * 60 * 1000))

            if (daysDiff <= 90) {
                const label = daysDiff === 1 ? 'Day' : 'Days'
                return `${daysDiff} ${label}`
            }

            let months = (today.getFullYear() - birth.getFullYear()) * 12 +
                (today.getMonth() - birth.getMonth())
            if (today.getDate() < birth.getDate()) months--

            if (months < 12) {
                const label = months === 1 ? 'Month' : 'Months'
                return `${months} ${label}`
            }

            const years = Math.floor(months / 12)
            if (years >= 100) return null
            const yearLabel = years === 1 ? 'Year' : 'Years'
            return `${years} ${yearLabel}`
        }
    }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/distributorVariables/variable.scss';

.date-field-container {
    position: relative;
}

.custom-datepicker {
    width: 100%;
}

.date-field-container :deep(.p-datepicker .p-inputtext) {
    padding-right: 108px;
    font-size: 12px;
}

.age-display {
    position: absolute;
    top: 50%;
    right: 45px;
    transform: translateY(-50%);
    z-index: 9;
    pointer-events: none;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    max-width: calc(100% - 120px);
    padding: 2px 8px;
    line-height: 1.2;
    border-radius: 4px;
    background-color: $gray-100;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
